import React, { createContext, useContext, useMemo, useState } from 'react';
import type { ApolloError } from 'apollo-client';

import { useSuggestedTemplates } from './useSuggestedTemplates';
import type { SuggestedTemplatesQuery_allTemplates_nodes as Template } from './queries/__types__/SuggestedTemplatesQuery.ts';
import type { BLANK_PAGE_QUICK_ACTIONS_MULTI_VARIANT_COHORTS } from './bpqaConst';

type BlankPageQuickActionsContextType = {
	templates: Template[];
	templateIndex: number;
	buttonHovered: boolean;
	loading: boolean;
	error: ApolloError | undefined;
	setButtonHovered: (value: boolean) => void;
	setTemplateIndex: (value: number) => void;
};

export const BlankPageQuickActionsContext = createContext<BlankPageQuickActionsContextType>({
	templates: [],
	templateIndex: 0,
	buttonHovered: false,
	loading: false,
	error: undefined,
	setButtonHovered: () => {},
	setTemplateIndex: () => {},
});

type BlankPageQuickActionsProviderProps = {
	children: React.ReactNode;
	cohort: BLANK_PAGE_QUICK_ACTIONS_MULTI_VARIANT_COHORTS;
	spaceKey: string;
};

export const BlankPageQuickActionsProvider: React.FC<BlankPageQuickActionsProviderProps> = ({
	children,
	cohort,
	spaceKey,
}) => {
	const [templateIndex, setTemplateIndex] = useState<number>(0);
	const [buttonHovered, setButtonHovered] = useState<boolean>(false);

	const { error, loading, templates } = useSuggestedTemplates({
		cohort,
		spaceKey,
	});

	const contextValue = useMemo(() => {
		return {
			templates,
			error,
			loading,
			templateIndex,
			setTemplateIndex,
			buttonHovered,
			setButtonHovered,
		};
	}, [templates, error, loading, templateIndex, setTemplateIndex, buttonHovered, setButtonHovered]);

	return (
		<BlankPageQuickActionsContext.Provider value={contextValue}>
			{children}
		</BlankPageQuickActionsContext.Provider>
	);
};

export const useBlankPageQuickActionsContext = () => useContext(BlankPageQuickActionsContext);
